import React from 'react';
import { inject, observer } from 'mobx-react';
import type { AppStore } from '@stores/app/store';
import PCFeedbackProblemPicker from './pc/index';
import H5FeedbackProblemPicker from './h5/index';

export interface FeedbackProblemItem {
    id: string | number;
    name: string;
    value: string;
    /** 该问题类型下的反馈数量 */
    feedbackCount?: number;
    /** 该问题类型下已解决问题数量 */
    solvedCount?: number;
}

export interface FeedbackProblemCategory {
    id: string | number;
    name: string;
    problems: FeedbackProblemItem[];
}

export interface FeedbackProblemPickerProps {
    visible: boolean;
    categories: FeedbackProblemCategory[];
    value?: string;
    onClose: () => void;
    onConfirm: (problem: FeedbackProblemItem | null) => void;
    /** 转换流程使用取消，筛选入口默认仍为重置。 */
    secondaryAction?: 'reset' | 'cancel';
    /** 转帖弹窗使用独立间距，不改变发帖页已有布局。 */
    variant?: 'default' | 'conversion';
    requireSelection?: boolean;
}

/** 将接口返回的单条问题类型转为统一结构 */
const parseProblemItem = (item: any, index: number): FeedbackProblemItem => ({
    id: item.id ?? item.problemId ?? index,
    name: item.name || item.title || item.label || item.problemName || `类型${index + 1}`,
    value: String(item.id ?? item.problemId ?? item.value ?? ''),
    feedbackCount: Number(item.feedbackCount ?? item.feedback_count ?? 0) || 0,
    solvedCount: Number(item.solvedCount ?? item.solved_count ?? 0) || 0,
});

/** 兼容多种子级字段名：children / problems / items 等 */
const getCategoryChildren = (category: any) => (
    category.children
    || category.problems
    || category.items
    || category.list
    || category.subList
    || []
);

/**
 * 解析 /api/v3/feedbacks/problems 响应。
 * 支持「分类 + 子项」嵌套结构，也兼容扁平列表。
 */
export const parseProblemCategories = (res: any): FeedbackProblemCategory[] => {
    if (!res || res.code !== 0) {
        return [];
    }

    const rawList = res?.data?.data || res?.data?.list || res?.data || [];
    if (!Array.isArray(rawList) || rawList.length === 0) {
        return [];
    }

    const hasNestedCategories = rawList.some((item) => {
        const children = getCategoryChildren(item);
        return Array.isArray(children) && children.length > 0;
    });

    if (hasNestedCategories) {
        return rawList.map((category: any, categoryIndex: number) => {
            const children = getCategoryChildren(category);
            return {
                id: category.id ?? category.categoryId ?? categoryIndex,
                name: category.name || category.title || category.categoryName || `分类${categoryIndex + 1}`,
                problems: (Array.isArray(children) ? children : [])
                    .map(parseProblemItem)
                    .filter((item: FeedbackProblemItem) => item.value !== ''),
            };
        }).filter((category: FeedbackProblemCategory) => category.problems.length > 0);
    }

    const problems = rawList.map(parseProblemItem).filter((item: FeedbackProblemItem) => item.value !== '');
    if (!problems.length) {
        return [];
    }

    // 扁平列表兜底：不展示分类标题，直接渲染一组标签
    return [{
        id: 'all',
        name: '',
        problems,
    }];
};

/** 将所有分类下的问题类型拍平为一维列表（去重） */
const flattenProblemCategories = (categories: FeedbackProblemCategory[]): FeedbackProblemItem[] => {
    const result: FeedbackProblemItem[] = [];
    categories.forEach((category) => {
        category.problems.forEach((problem) => {
            if (!result.some((item) => item.value === problem.value)) {
                result.push(problem);
            }
        });
    });
    return result;
};

/** 根据 value 从分类数据中查找已选问题类型 */
export const findProblemByValue = (
    categories: FeedbackProblemCategory[],
    value?: string | number,
): FeedbackProblemItem | null => {
    if (value === undefined || value === null || value === '') return null;
    const normalized = String(value);
    return flattenProblemCategories(categories).find((item) => item.value === normalized) || null;
};

interface FeedbackProblemPickerWrapperProps extends FeedbackProblemPickerProps {
    app?: AppStore;
}

/** 问题类型选择器：PC 居中弹窗 / H5 底部抽屉，按平台自动切换 */
@inject('app')
@observer
class FeedbackProblemPicker extends React.Component<FeedbackProblemPickerWrapperProps> {
    /** 按当前平台渲染 PC 或 H5 选择器 */
    render() {
        const { app, ...props } = this.props;
        const platform = app?.platform || 'h5';
        if (platform === 'pc') {
            return <PCFeedbackProblemPicker {...props} />;
        }
        return <H5FeedbackProblemPicker {...props} />;
    }
}

export default FeedbackProblemPicker;
